/* eslint-disable prettier/prettier */
import { keyframes } from '@emotion/react'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import KeyIcon from '@mui/icons-material/Key'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import AIService from '../../services/aiService'
import { errorMessage } from '../../services/axios/errorMessage'
import ConfirmDialog from '../../components/shared/ConfirmDialog'
import ErrorBanner from '../../components/shared/ErrorBanner'
import InlineLoading from '../../components/shared/InlineLoading'
import SingleSelect from '../../components/SingleSelect'
import SendingDomainsCard from './SendingDomainsCard'
import SenderEmails from '../../components/SenderEmails'
import {
    ACCENT,
    BRAND,
    FONT,
    GRADIENT,
    ICON,
    ON_ORANGE,
    RADIUS,
    SETTINGS,
    SHADOW,
    STATUS,
    SURFACE,
    SYSTEM,
    TEXT,
    tint,
} from '../../theme/tokens'

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`
const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 ${tint(SYSTEM.success, 0.4)}; }
  50%       { box-shadow: 0 0 0 6px ${tint(SYSTEM.success, 0)}; }
`
const borderGlow = keyframes`
  0%, 100% { border-color: ${tint(
      BRAND.orange,
      0.4
  )}; box-shadow: 0 0 12px ${tint(BRAND.orange, 0.1)}; }
  50%       { border-color: ${tint(
      BRAND.orange,
      0.8
  )}; box-shadow: 0 0 20px ${tint(BRAND.orange, 0.25)}; }
`

const BG = {
    margin: '-20px -24px',
    minHeight: 'calc(100vh - 48px)',
    background:
        `radial-gradient(ellipse at 30% 40%, ${SETTINGS.glow} 0%, transparent 55%),` +
        `radial-gradient(ellipse at 80% 80%, ${tint(
            BRAND.funBlue,
            0.05
        )} 0%, transparent 45%),` +
        SURFACE.page,
    padding: { xs: '24px 16px', md: '32px 24px' },
}

// Cards sit in a centred, width-capped column so they read as one panel
// instead of floating across a wide viewport.
const COLUMN = {
    width: '100%',
    maxWidth: 1160,
    mx: 'auto',
}

const CARD = {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: SETTINGS.cardGround,
    border: `1px solid ${SURFACE.border}`,
    borderRadius: `${RADIUS.lg}px`,
    boxShadow: SHADOW.card,
    overflow: 'hidden',
    animation: `${fadeIn} 0.4s ease both`,
}

const secretInputSx = {
    typography: 'body2',
    fontFamily: FONT.mono,
    borderRadius: `${RADIUS.sm}px`,
    // The key field shares a row with Save key and Remove: one height for all.
    height: 38,
    '&.Mui-focused': {
        animation: `${borderGlow} 2s ease-in-out infinite`,
    },
}

const saveButtonSx = {
    background: GRADIENT.brand,
    borderRadius: `${RADIUS.sm}px`,
    fontWeight: 600,
    typography: 'body2',
    // Explicit height so this button lines up with the key field and Remove
    // (which has a startIcon and otherwise renders 2px taller).
    height: 38,
    minWidth: 120,
    px: 3,
    whiteSpace: 'nowrap',
    py: 0,
    boxShadow: `0 0 20px ${ACCENT.border30}`,
    '&:hover': {
        // Lighter on hover, as the contained primary button: Deep Blue type
        // is 3.6:1 on a darker orange, 6.0:1 on the tint.
        background: `linear-gradient(135deg, ${BRAND.orangeTint}, ${BRAND.yellow})`,
        boxShadow: `0 0 30px ${tint(BRAND.orange, 0.5)}`,
    },
    '&.Mui-disabled': {
        background: SURFACE.sunken,
        color: TEXT.disabled,
    },
}

const removeButtonSx = {
    borderColor: STATUS.error.border,
    color: STATUS.error.fg,
    borderRadius: `${RADIUS.sm}px`,
    fontWeight: 500,
    // Match saveButtonSx's box so both buttons share a height and baseline:
    // the outlined variant's 1px border is absorbed via box-sizing.
    typography: 'body2',
    height: 38,
    py: 0,
    flexShrink: 0,
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
    '&:hover': {
        borderColor: STATUS.error.fg,
        background: STATUS.error.bg,
    },
}

const SecretField = ({ placeholder, value, onChange, onEnter, label }) => {
    const [show, setShow] = useState(false)
    return (
        <TextField
            fullWidth
            placeholder={placeholder}
            type={show ? 'text' : 'password'}
            value={value}
            onChange={onChange}
            onKeyDown={(e) => e.key === 'Enter' && onEnter?.()}
            inputProps={{ 'aria-label': label }}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton
                            size="small"
                            aria-label={
                                show ? `Hide ${label}` : `Show ${label}`
                            }
                            onClick={() => setShow((v) => !v)}
                            sx={{
                                color: TEXT.muted,
                                '&:hover': { color: BRAND.orange },
                            }}
                        >
                            {show ? (
                                <VisibilityOffIcon sx={{ fontSize: ICON.md }} />
                            ) : (
                                <VisibilityIcon sx={{ fontSize: ICON.md }} />
                            )}
                        </IconButton>
                    </InputAdornment>
                ),
                sx: secretInputSx,
            }}
        />
    )
}
SecretField.propTypes = {
    placeholder: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    onEnter: PropTypes.func,
    label: PropTypes.string,
}

const CardShell = ({ icon, title, subtitle, footer, children }) => (
    <Box component="section" aria-label={title} sx={CARD}>
        <Box
            sx={{
                px: 3,
                py: 2,
                minHeight: 76,
                boxSizing: 'border-box',
                borderBottom: `1px solid ${ACCENT.bg15}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                background: tint(BRAND.orange, 0.05),
            }}
        >
            <Box
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: `${RADIUS.sm}px`,
                    background: GRADIENT.brand,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 0 16px ${tint(BRAND.orange, 0.4)}`,
                }}
            >
                {icon}
            </Box>
            <Box>
                <Typography
                    sx={{
                        typography: 'h6',
                        fontWeight: 700,
                        color: TEXT.primary,
                        lineHeight: 1.2,
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    sx={{
                        typography: 'caption',
                        color: TEXT.muted,
                        mt: 0.25,
                        lineHeight: 1.35,
                    }}
                >
                    {subtitle}
                </Typography>
            </Box>
        </Box>
        <Box sx={{ p: 3, flex: 1 }}>{children}</Box>
        <Box
            sx={{
                px: 3,
                py: 1.5,
                mt: 'auto',
                borderTop: `1px solid ${tint(BRAND.orange, 0.1)}`,
                background: SURFACE.subtle,
            }}
        >
            <Typography
                sx={{
                    typography: 'caption',
                    color: TEXT.faint,
                    lineHeight: 1.5,
                }}
            >
                {footer}
            </Typography>
        </Box>
    </Box>
)
CardShell.propTypes = {
    icon: PropTypes.node,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    footer: PropTypes.string,
    children: PropTypes.node,
}

const ConnectedBanner = ({ text }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            borderRadius: `${RADIUS.sm}px`,
            background: STATUS.success.bg,
            border: `1px solid ${STATUS.success.border}`,
            mb: 2.5,
        }}
    >
        <Box
            sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: STATUS.success.fg,
                flexShrink: 0,
                animation: `${pulse} 2s ease-in-out infinite`,
            }}
        />
        <Typography
            sx={{
                typography: 'caption',
                color: STATUS.success.fg,
                fontFamily: FONT.mono,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}
        >
            {text}
        </Typography>
        <CheckCircleIcon sx={{ fontSize: ICON.md, color: STATUS.success.fg }} />
    </Box>
)
ConnectedBanner.propTypes = { text: PropTypes.string }

/* ── AI providers ──────────────────────────────────────────── */
// The AI tools run on Claude or OpenAI (GET /comms/ai/settings/status says
// which, and each provider's key state). Order = the API's default order.
const PROVIDERS = [
    {
        name: 'anthropic',
        title: 'Claude API Key',
        subtitle: 'An Anthropic workspace key (not a personal user key)',
        placeholder: 'sk-ant-api03-...',
    },
    {
        name: 'openai',
        title: 'OpenAI API Key',
        subtitle:
            'Optional: without one, the comms service key (OPENAI_API_KEY_COMMS) is used',
        placeholder: 'sk-proj-...',
    },
]

/**
 * One provider's key state from the status. A service-comms build from before
 * OpenAI answers only `{configured, demo_mode}` (about the Claude key); read
 * that as the Claude card's state rather than leaving the card spinning.
 */
export const providerInfo = (status, name) => {
    if (!status) return undefined
    if (status.providers) return status.providers[name]
    if (name !== 'anthropic')
        return { label: 'OpenAI', configured: false, source: null }
    return {
        label: 'Claude',
        configured: Boolean(status.configured),
        source: status.configured ? 'settings' : null,
    }
}

/** What the key banner says for a provider's key `source`. */
export const keySourceText = (source) => {
    if (source === 'settings') return 'Key saved in Settings'
    if (source === 'service')
        return 'Using the comms service key (OPENAI_API_KEY_COMMS)'
    return ''
}

/**
 * A provider's line in the AI provider dropdown: its label and model, then
 * "in use" for the provider the AI tools run on, or "no key yet" for one that
 * cannot be used until a key is saved ("Claude · claude-sonnet-4-6 · in use").
 */
export const providerOptionLabel = (status, name) => {
    const info = providerInfo(status, name) ?? {}
    let state = ''
    if (status?.provider === name) state = 'in use'
    else if (!info.configured) state = 'no key yet'
    return [info.label ?? name, info.model, state].filter(Boolean).join(' · ')
}

/**
 * The model switch: a dropdown of the providers, then one of the selected
 * provider's models. Picking a provider shows its key card below; picking one
 * that has a key also switches every AI tool to it (the page does that, see
 * Settings). A provider with no key is shown with a note, since the API
 * refuses to switch to it until its key is saved. Picking a model makes the
 * AI tools use it whenever that provider is in use.
 */
const ProviderSwitch = ({
    status,
    selected,
    switching,
    error,
    onSelect,
    savingModel,
    onModelChange,
}) => {
    const label = (name) => providerInfo(status, name)?.label ?? name
    // A service-comms build from before the model choice sends no `models`.
    const models = status?.models?.[selected] ?? []
    const selectedHasNoKey =
        Boolean(status?.provider) &&
        selected !== status.provider &&
        !providerInfo(status, selected)?.configured

    return (
        <Box sx={{ ...CARD, height: 'auto', p: 3 }}>
            <Typography
                component="h2"
                sx={{ typography: 'h6', fontWeight: 700, color: TEXT.primary }}
            >
                Model for the AI tools
            </Typography>
            <Typography
                sx={{
                    typography: 'caption',
                    color: TEXT.secondary,
                    mt: 0.5,
                    mb: 2,
                }}
            >
                Template Writer, variants, translation, CSV validation, campaign
                briefs, insights, risk scoring and Comms Finder all use the
                provider chosen here.
            </Typography>
            {/* Until the status is in, every option would read "no key yet". */}
            {!status && (
                <InlineLoading
                    label="Checking the AI settings…"
                    justify="flex-start"
                    py={1.5}
                />
            )}
            {status && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        maxWidth: 440,
                    }}
                >
                    <SingleSelect
                        label="AI provider"
                        value={selected}
                        options={PROVIDERS.map(({ name }) => ({
                            value: name,
                            label: providerOptionLabel(status, name),
                        }))}
                        optionValueText="value"
                        optionLabelText="label"
                        handleChange={onSelect}
                        disabled={Boolean(switching)}
                    />
                    {switching && (
                        <CircularProgress
                            size={16}
                            aria-label={`Switching the AI tools to ${label(
                                switching
                            )}`}
                            sx={{
                                color: SETTINGS.switchSpinner,
                                flexShrink: 0,
                            }}
                        />
                    )}
                </Box>
            )}
            {status && models.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        maxWidth: 440,
                        mt: 2,
                    }}
                >
                    <SingleSelect
                        label={`${label(selected)} model`}
                        value={providerInfo(status, selected)?.model ?? ''}
                        options={models.map(({ id, label: name }) => ({
                            value: id,
                            label: `${name} (${id})`,
                        }))}
                        optionValueText="value"
                        optionLabelText="label"
                        handleChange={onModelChange}
                        disabled={savingModel || Boolean(switching)}
                    />
                    {savingModel && (
                        <CircularProgress
                            size={16}
                            aria-label={`Changing the ${label(selected)} model`}
                            sx={{
                                color: SETTINGS.switchSpinner,
                                flexShrink: 0,
                            }}
                        />
                    )}
                </Box>
            )}
            {status && !status.provider && (
                <Typography
                    variant="body2"
                    sx={{ color: SETTINGS.providerNote, mt: 2 }}
                >
                    No provider has a key yet: save one below for the provider
                    chosen here.
                </Typography>
            )}
            {selectedHasNoKey && (
                <Typography
                    variant="body2"
                    sx={{ color: SETTINGS.providerNote, mt: 2 }}
                >
                    {label(selected)} has no key yet. Save one below to switch
                    the AI tools to it; they stay on {label(status.provider)}{' '}
                    until then.
                </Typography>
            )}
            {error && (
                <Box sx={{ mt: 2 }}>
                    <ErrorBanner message={error} />
                </Box>
            )}
        </Box>
    )
}
ProviderSwitch.propTypes = {
    status: PropTypes.object,
    selected: PropTypes.string.isRequired,
    switching: PropTypes.string,
    error: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    savingModel: PropTypes.bool,
    onModelChange: PropTypes.func.isRequired,
}

/* ── The selected provider's API key card ────────────────────── */
const ProviderKeyCard = ({ provider, info, onSaved, onRemoved }) => {
    const [apiKey, setApiKey] = useState('')
    const [saving, setSaving] = useState(false)
    const [removing, setRemoving] = useState(false)
    const [error, setError] = useState('')
    const [confirmOpen, setConfirmOpen] = useState(false)
    const label = info?.label ?? provider.name
    const savedHere = info?.source === 'settings'

    const handleSave = async () => {
        if (!apiKey.trim()) return
        setSaving(true)
        setError('')
        try {
            await AIService.saveKey(apiKey.trim(), provider.name)
            setApiKey('')
            onSaved(provider.name)
        } catch (err) {
            // The API checks the key with the provider first; show its reason
            // (e.g. "Claude did not accept this key: ...").
            setError(errorMessage(err?.response))
        } finally {
            setSaving(false)
        }
    }

    const handleRemove = async () => {
        setConfirmOpen(false)
        setRemoving(true)
        setError('')
        try {
            await AIService.deleteKey(provider.name)
            onRemoved(provider.name)
        } catch (err) {
            setError(errorMessage(err?.response))
        } finally {
            setRemoving(false)
        }
    }

    return (
        <CardShell
            icon={<KeyIcon sx={{ fontSize: ICON.md, color: ON_ORANGE }} />}
            title={provider.title}
            subtitle={provider.subtitle}
            footer="A key saved here is checked with the provider, then written to AWS SSM Parameter Store as a SecureString. It is never logged or returned to the client."
        >
            {!info && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={20} sx={{ color: BRAND.orange }} />
                </Box>
            )}

            {info?.model && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 1,
                        mb: 2,
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{ color: SETTINGS.modelLabel }}
                    >
                        Model
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: SETTINGS.model, fontFamily: FONT.mono }}
                    >
                        {info.model}
                    </Typography>
                </Box>
            )}

            {info?.configured && (
                <ConnectedBanner text={keySourceText(info.source)} />
            )}

            {info && (
                <>
                    {savedHere && (
                        <Typography
                            sx={{
                                typography: 'caption',
                                color: TEXT.muted,
                                mb: 1.5,
                            }}
                        >
                            Enter a new key to overwrite the saved one.
                        </Typography>
                    )}
                    {/* One row on a wide card (the field takes the room, the
                        buttons keep their natural width); stacked on a phone. */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 1.5,
                            alignItems: { xs: 'stretch', sm: 'center' },
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <SecretField
                                placeholder={provider.placeholder}
                                label={`${label} API key`}
                                value={apiKey}
                                onChange={(e) => setApiKey(e.target.value)}
                                onEnter={handleSave}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Button
                                variant="contained"
                                disabled={!apiKey.trim() || saving}
                                onClick={handleSave}
                                sx={{
                                    ...saveButtonSx,
                                    flex: { xs: 1, sm: 'none' },
                                }}
                            >
                                {saving ? (
                                    <CircularProgress
                                        size={14}
                                        sx={{ color: TEXT.primary }}
                                    />
                                ) : (
                                    'Save key'
                                )}
                            </Button>
                            {savedHere && (
                                <Button
                                    variant="outlined"
                                    disabled={removing}
                                    onClick={() => setConfirmOpen(true)}
                                    startIcon={
                                        <DeleteOutlineIcon
                                            sx={{ fontSize: ICON.sm }}
                                        />
                                    }
                                    sx={removeButtonSx}
                                >
                                    {removing ? (
                                        <CircularProgress
                                            size={13}
                                            sx={{ color: STATUS.error.fg }}
                                        />
                                    ) : (
                                        'Remove'
                                    )}
                                </Button>
                            )}
                        </Box>
                    </Box>
                    {error && (
                        <Box sx={{ mt: 2 }}>
                            <ErrorBanner message={error} />
                        </Box>
                    )}
                </>
            )}

            <ConfirmDialog
                open={confirmOpen}
                title={`Remove the ${label} API key?`}
                message={
                    provider.name === 'openai'
                        ? 'The OpenAI key saved here is deleted; the AI tools then use the comms service key.'
                        : 'The Claude key is deleted. If Claude is in use, the AI tools move to OpenAI when it has a key, and stop otherwise until a key is saved.'
                }
                confirmLabel="Remove key"
                destructive
                onConfirm={handleRemove}
                onCancel={() => setConfirmOpen(false)}
            />
        </CardShell>
    )
}
ProviderKeyCard.propTypes = {
    provider: PropTypes.shape({
        name: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        subtitle: PropTypes.string,
        placeholder: PropTypes.string,
    }).isRequired,
    info: PropTypes.object,
    onSaved: PropTypes.func.isRequired,
    onRemoved: PropTypes.func.isRequired,
}

const Settings = () => {
    const [status, setStatus] = useState(null)
    const [statusError, setStatusError] = useState('')
    // The provider picked in the dropdown; empty follows the provider in use
    // (the default, and where a refused switch goes back to).
    const [picked, setPicked] = useState('')
    const [switching, setSwitching] = useState('')
    const [switchError, setSwitchError] = useState('')
    const [savingModel, setSavingModel] = useState(false)

    /** Read the AI status into the page; resolves once it is in (or failed). */
    const loadStatus = useCallback(
        () =>
            AIService.getStatus()
                .then((res) => {
                    setStatus(res.data)
                    setStatusError('')
                })
                .catch((err) => setStatusError(errorMessage(err?.response))),
        []
    )

    useEffect(() => {
        loadStatus()
    }, [loadStatus])

    const selected = picked || status?.provider || PROVIDERS[0].name
    const selectedProvider =
        PROVIDERS.find(({ name }) => name === selected) ?? PROVIDERS[0]

    /**
     * Switch every AI tool to `name` and show the status the API answers. A
     * refusal shows its reason and puts the dropdown back on the provider in
     * use. Resolves to whether the switch went through.
     */
    const switchTo = async (name) => {
        setSwitching(name)
        setSwitchError('')
        try {
            const res = await AIService.setProvider(name)
            setStatus(res.data)
            return true
        } catch (err) {
            setSwitchError(errorMessage(err?.response))
            setPicked('')
            return false
        } finally {
            setSwitching('')
        }
    }

    /**
     * A provider picked in the dropdown: show its card, and switch to it at
     * once when it has a key. One with no key is only shown (the API would
     * refuse the switch); saving its key switches to it.
     */
    const handleSelect = (name) => {
        if (!name || name === selected) return
        setPicked(name)
        setSwitchError('')
        if (name !== status?.provider && providerInfo(status, name)?.configured)
            switchTo(name)
    }

    /**
     * A model picked for the provider on screen: the AI tools use it whenever
     * that provider is in use. A refusal shows its reason.
     */
    const handleModelChange = async (model) => {
        if (!model || model === providerInfo(status, selected)?.model) return
        setSavingModel(true)
        setSwitchError('')
        try {
            const res = await AIService.setModel(selected, model)
            setStatus(res.data)
        } catch (err) {
            setSwitchError(errorMessage(err?.response))
        } finally {
            setSavingModel(false)
        }
    }

    /**
     * A key was saved for `name`, the provider on screen: the person chose
     * it, so switch the AI tools to it when it is not already in use, then
     * reload the status (the card then says where its key comes from). An
     * API from before OpenAI has no provider switch, so only the reload runs.
     */
    const handleKeySaved = async (name) => {
        if (status?.providers && name !== status.provider) await switchTo(name)
        await loadStatus()
    }

    /**
     * A key was removed: stay on that provider's card (its note then says
     * which provider the AI tools moved to) and reload the status.
     */
    const handleKeyRemoved = (name) => {
        setPicked(name)
        loadStatus()
    }

    return (
        <Box sx={BG}>
            <Box sx={{ ...COLUMN, mb: 3 }}>
                <Typography
                    sx={{
                        typography: 'h2',
                        fontWeight: 700,
                        color: TEXT.primary,
                        lineHeight: 1.2,
                    }}
                >
                    Settings
                </Typography>
                <Typography
                    sx={{ typography: 'body2', color: TEXT.secondary, mt: 0.5 }}
                >
                    The AI Suite&apos;s model and API keys, and sending-domain
                    warmup.
                </Typography>
            </Box>
            <Box sx={{ ...COLUMN, mb: { xs: 2.5, md: 3 } }}>
                {statusError ? (
                    <ErrorBanner message={statusError} />
                ) : (
                    <ProviderSwitch
                        status={status}
                        selected={selected}
                        switching={switching}
                        error={switchError}
                        onSelect={handleSelect}
                        savingModel={savingModel}
                        onModelChange={handleModelChange}
                    />
                )}
            </Box>
            {/* Only the provider chosen above: keyed by it, so a key typed for
                one provider never carries over to the other's card. */}
            <Box sx={COLUMN}>
                <ProviderKeyCard
                    key={selectedProvider.name}
                    provider={selectedProvider}
                    info={providerInfo(status, selectedProvider.name)}
                    onSaved={handleKeySaved}
                    onRemoved={handleKeyRemoved}
                />
            </Box>
            {/* Warmup is operational config rather than a credential, so it sits
                below the credential card and spans the full width -- the domain
                rows need the room. */}
            <Box
                sx={{
                    ...COLUMN,
                    mt: 3,
                    p: 3,
                    background: SETTINGS.cardGround,
                    border: `1px solid ${SURFACE.border}`,
                    borderRadius: `${RADIUS.lg}px`,
                    boxShadow: SHADOW.card,
                }}
            >
                <SendingDomainsCard />
            </Box>
            <Box
                sx={{
                    ...COLUMN,
                    mt: 3,
                    p: 3,
                    background: SETTINGS.cardGround,
                    border: `1px solid ${SURFACE.border}`,
                    borderRadius: `${RADIUS.lg}px`,
                    boxShadow: SHADOW.card,
                }}
            >
                <SenderEmails />
            </Box>
        </Box>
    )
}

export default Settings
